import { cn } from "@/lib/utils";
import { type BlogPost } from "@/lib/blogs";
import { Link } from "react-router-dom";

type BlogCardProps = {
  post: BlogPost;
  featured?: boolean;
};

export function BlogCard({ post, featured = false }: BlogCardProps) {
  const articleClassName = cn(
    featured
      ? "grid grid-cols-[0.5fr_1fr] gap-4 p-6 rounded-3xl bg-white max-[991px]:grid-cols-1 max-[479px]:p-4"
      : "flex flex-col justify-center items-start h-full p-6 rounded-3xl bg-white max-[479px]:p-4",
  );
  const titleClassName = cn(
    "m-0",
    featured
      ? "font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] font-medium leading-[1.15] tracking-[-0.02em] text-pink-darker"
      : "font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-[1.2] text-pink-darker",
  );

  return (
    <article className={articleClassName}>
      <Link
        to={`/blog-post/${post.slug}`}
        className={cn("block", !featured && "max-[767px]:w-full w-1/2")}
        aria-label={post.title}
      >
        <div
          className={cn(
            "aspect-square rounded-2xl overflow-clip",
            featured && "max-[991px]:aspect-[1.12]",
          )}
        >
          <img src={post.listingImage || post.heroImage} alt="" className="object-cover w-full h-full" loading="lazy" />
        </div>
      </Link>

      <div className="flex w-full flex-1 flex-col justify-center items-start">
        <div className="spacer-medium" />
        <span className="inline border border-pink-darker rounded-full text-pink-darker py-1 px-3 font-display text-base font-bold">
          {post.category}
        </span>
        <div className="spacer-small" />

        <Link to={`/blog-post/${post.slug}`} className="text-inherit">
          {featured ? <h2 className={titleClassName}>{post.title}</h2> : <h3 className={titleClassName}>{post.title}</h3>}
        </Link>

        {post.excerpt ? (
          <>
            <div className="spacer-xsmall" />
            <p className="m-0 text-[#4a4a4a] line-clamp-4">{post.excerpt}</p>
          </>
        ) : null}

        <div className="spacer-medium" />

        <div className="flex items-center gap-1 mt-auto text-sm">
          <div>{post.listingDate}</div>
          {post.readTime ? (
            <>
              <div className="aspect-square w-[0.38rem] rounded-full bg-pink-darker" />
              <div className="font-semibold uppercase text-pink-dark">{post.readTime}</div>
            </>
          ) : null}
        </div>

        <div className="spacer-small" />

        <Link
          to={`/blog-post/${post.slug}`}
          className="inline-flex gap-2 bg-pink-dark text-white text-center cursor-pointer border-0 rounded-2xl justify-center items-center py-2 px-5 text-[18px] leading-[18px] font-medium no-underline transition-transform duration-200 ease-[cubic-bezier(0.39,0.575,0.565,1)] hover:-translate-y-[5%] active:scale-95"
        >
          <div>Read</div>
        </Link>
      </div>
    </article>
  );
}

export default BlogCard;
